import {
  Users,
  Swords,
  GitCompare,
  Calculator,
  Layout,
  LayoutGrid,
  Download,
  Library,
  BarChart3,
  Dice1,
  ScrollText,
} from "lucide-react";
import type { LucideIcon } from "lucide-react";

export interface NavigationItem {
  icon: LucideIcon;
  label: string;
  path: string;
  /** Short line shown on the home page */
  description?: string;
  /** Experimental feature — show a beta marker in navigation */
  beta?: boolean;
}

export interface NavigationCategory {
  title: string;
  items: NavigationItem[];
}

/**
 * Navigation items grouped by category
 */
export const navigationCategories: NavigationCategory[] = [
  {
    title: "Units & Weapons",
    items: [
      {
        icon: Users,
        label: "Units",
        path: "/units",
        description: "Search and filter the full unit database.",
      },
      {
        icon: Swords,
        label: "Weapons",
        path: "/weapons",
        description: "Browse ammunition and weapon stats.",
      },
      {
        icon: GitCompare,
        label: "Unit Comparison",
        path: "/unit-comparison",
        description: "Compare units and weapons side by side.",
      },
    ],
  },
  {
    title: "Deck Management",
    items: [
      {
        icon: LayoutGrid,
        label: "Decks",
        path: "/decks",
        description: "Overview: builder, import, library, and drafter.",
      },
      {
        icon: Layout,
        label: "Deck Builder",
        path: "/deck-builder",
        description: "Build and edit lists for any division.",
      },
      {
        icon: Download,
        label: "Import Deck",
        path: "/deck-import",
        description: "Paste a deck code to open in the builder.",
      },
      {
        icon: Library,
        label: "Deck Library",
        path: "/deck-library",
        description: "Community decks and deck detail pages.",
      },
      {
        icon: Dice1,
        label: "Deck Drafter",
        path: "/deck-drafter",
        description: "Draft a deck with guided random choices.",
      },
    ],
  },
  {
    title: "Analysis & Tools",
    items: [
      {
        icon: Calculator,
        label: "Damage Calculator",
        path: "/damage-calculator",
        description: "Theorycraft shots with range, terrain, and traits.",
      },
      {
        icon: BarChart3,
        label: "Divisions",
        path: "/divisions",
        description: "Community write-ups, map, and briefing metrics per division.",
      },
    ],
  },
  {
    title: "Information",
    items: [
      {
        icon: ScrollText,
        label: "Patch Notes",
        path: "/patch-notes",
        description: "Game patch history and field changes.",
      },
    ],
  },
];

/**
 * Whether the current path should highlight this nav item (including nested routes).
 */
export function isNavigationItemActive(pathname: string, item: NavigationItem): boolean {
  if (pathname === item.path) return true;

  const prefixMatchers: Record<string, (p: string) => boolean> = {
    "/units": (p) => p.startsWith("/units/"),
    "/weapons": (p) => p.startsWith("/weapons/"),
    "/deck-builder": (p) => p.startsWith("/deck-builder"),
    "/damage-calculator": (p) => p.startsWith("/damage-calculator"),
    "/divisions": (p) => p === "/divisions" || p.startsWith("/divisions/"),
    "/decks": (p) => p === "/decks",
    "/deck-library": (p) => /^\/deck\/[^/]+$/.test(p),
  };

  const match = prefixMatchers[item.path];
  return match ? match(pathname) : false;
}

/**
 * Public navigation items that should appear in the main menu and home page
 * Flattened array for backward compatibility
 */
export const navigationItems: NavigationItem[] = navigationCategories.flatMap(
  (category) => category.items
);
